import { Plugin } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'
import { INLINE_LINK_ID, INLINE_LINK_MENU_PLUGIN_KEY } from './Constants'
import { InlineLinkMenuView } from './InlineLinkMenuView'
import { LinkPluginArgs } from './Types'

const SHOW_DELAY = 500
const HIDE_DELAY = 500
let menuEnterTimer: NodeJS.Timeout
let menuLeaveTimer: NodeJS.Timeout
let mouseLeave: (event: MouseEvent) => void

const handleMouseOverLinkMenu = (_event: MouseEvent) => {
  clearTimeout(menuLeaveTimer)
  document.getElementById(INLINE_LINK_ID)?.addEventListener('mouseleave', mouseLeave)
}

export const InlineLinkMenuPlugin = ({ article, layer }: LinkPluginArgs) => {
  return new Plugin({
    key: INLINE_LINK_MENU_PLUGIN_KEY,
    state: {
      init: () => {
        return { link: null, show: false, nodePos: null }
      },
      apply: (tr, pluginState) => {
        const meta = tr.getMeta(INLINE_LINK_MENU_PLUGIN_KEY)
        if (meta) {
          return { ...pluginState, ...meta }
        }
        return pluginState
      },
    },
    props: {
      handleDOMEvents: {
        click: (view: EditorView, event: MouseEvent) => {
          if ('pointerType' in event && event.pointerType !== 'mouse' && event.isTrusted) {
            // do not open link on click when using tough device
            event.preventDefault()
            return false
          }

          const possibleLink = checkForLinkMarkFromTarget(event.target as HTMLElement, view)
          if (!possibleLink) return false
          const link = possibleLink.target
          const url = link.getAttribute('href')
          const target = link.getAttribute('target')
          if (url && target) {
            window.open(url, target)
            return true
          } else if (url) {
            window.open(url, '_self')
            return true
          }
          return false
        },
        mouseover: (view: EditorView, event: MouseEvent) => {
          const link = checkForLinkMarkFromTarget(event.target as HTMLElement, view)
          if (!link) return false
          const { target, nodePos } = link
          if (mouseLeave) target.removeEventListener('mouseleave', mouseLeave)

          mouseLeave = (_event: MouseEvent) => {
            if (menuEnterTimer) clearTimeout(menuEnterTimer)
            menuLeaveTimer = setTimeout(() => {
              const tr = view.state.tr
              tr.setMeta(INLINE_LINK_MENU_PLUGIN_KEY, { show: false })
              view.dispatch(tr)
            }, HIDE_DELAY)
          }

          clearTimeout(menuLeaveTimer)
          target.addEventListener('mouseleave', mouseLeave)
          menuEnterTimer = setTimeout(() => {
            const tr = view.state.tr
            tr.setMeta(INLINE_LINK_MENU_PLUGIN_KEY, { link: target, show: true, nodePos })
            view.dispatch(tr)
            document.getElementById(INLINE_LINK_ID)?.addEventListener('mouseover', handleMouseOverLinkMenu)
          }, SHOW_DELAY)
          return false
        },
      },
    },
    view(view) {
      return new InlineLinkMenuView({ view, article, layer })
    },
  })
}

const checkForLinkMarkFromTarget = (
  target: HTMLElement,
  view: EditorView,
): { target: HTMLElement; nodePos: number } | null => {
  let link = target
  if (target.nodeName !== 'A') {
    if (target.closest('a')) link = target.closest('a') as HTMLElement
    if (!link || link.nodeName !== 'A') return null
  }
  const posAtDOM = view.posAtDOM(link, 0)
  if (posAtDOM < 0) return null
  const node = view.state.doc.nodeAt(posAtDOM)
  if (!node) return null
  const isProsemirrorLinkMark = view.state.schema.marks.link.isInSet(node.marks)
  if (!isProsemirrorLinkMark) return null
  return { target: link, nodePos: posAtDOM }
}
